import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  SdohPermissionsEnum,  
  WCCPermissionsEnum
} from '@core/constants/permissions.enum';
import { SecurityService } from '@mynexus/mynexus-ui-lib';
import { NavMenuItem } from './models/nav-menu-item.model';
import { of } from 'rxjs';
import { AuthenticationService } from '@core/authentication/services';

@Injectable({
  providedIn: 'root'
})
export class DashboardLayoutService {
  private readonly router = inject(Router);
  private readonly securityService = inject(SecurityService);
  private readonly authenticationService = inject(AuthenticationService);

  private activeNavLinkIndex = 0;

  private hasAnalyticAccess() {
    if (
      this.authenticationService.groups?.includes('SDoHProviderAdminExternal') ||
      this.authenticationService.groups?.includes('SpecialtyProgramProviderAdminExternal')
    ) {
      return true;
    } else return false;
  }

  private readonly _navMenuItems: NavMenuItem[] = [
    {
      label: 'Referral Dashboard',
      icon: 'pi pi-folder-open',
      isActive: false,
      url: '/',
      permissions: [],
      isHidden: of(false),
      action: () => this.redirectToHomePage()
    },
    {
      label: 'SDoH Analytics Overview',
      icon: 'pi pi-chart-bar',
      isActive: false,
      url: '/analytics/sdoh',
      permissions: [SdohPermissionsEnum.ANALYTICS_READ],
      isHidden: of(!this.hasAnalyticAccess()),
      action: () => {
        this.updateNavSelection(1);
        this.router.navigate(['/analytics/sdoh']);
      }
    },
    {
      label: 'WCC Analytics Overview',
      icon: 'pi pi-chart-bar',
      isActive: false,
      url: '/wcc/analytics',
      permissions: [WCCPermissionsEnum.Analytics_Read],
      isHidden: of(!this.hasAnalyticAccess()),
      action: () => {
        this.updateNavSelection(2);
        this.router.navigate(['/wcc/analytics']);
      }
    }
  ];

  private sdohTabAccess = this.securityService.hasAccess([SdohPermissionsEnum.ASSESSMENT_READ]);  

  private wccTabAccess = this.securityService.hasAccess([WCCPermissionsEnum.Assessment_Read]);

  private updateNavSelection(index: number): void {
    this.activeNavLinkIndex = index;
    this.navMenuItems = this._navMenuItems.map((item, idx) => ({
      ...item,
      isActive: idx === this.activeNavLinkIndex
    }));
  }

  navMenuItems = this.checkNavMenuPermissions().map((item, index) => ({
    ...item,
    isActive: index === this.activeNavLinkIndex
  }));

  public redirectToDefaultPage(): void {
    let url = this.router.url;

    if (url === '/') {
      url = this.getPageUrl();
    }

    this.activateNavLink(url);
    this.router.navigateByUrl(url);
  }

  activateNavLink(url: string): void {
    const activeLinkIndex = this._navMenuItems.findIndex(item => item.url === url);
    this.activeNavLinkIndex = activeLinkIndex > 0 ? activeLinkIndex : 0;
  }

  public redirectToHomePage(): void {
    const defaultHomeLink: string = this.getPageUrl();
    this.updateNavSelection(0);
    this.router.navigate([defaultHomeLink]);
  }

  private checkNavMenuPermissions(): NavMenuItem[] {
    return this._navMenuItems.filter(
      link => link.permissions.length === 0 || this.securityService.hasAccess(link.permissions)
    );
  }

  private getPageUrl() {
    switch (true) {
      case this.sdohTabAccess:
        return '/sdoh';
      case this.wccTabAccess:
        return '/wcc';

      default:
        return '/access-denied';
    }
  }
}
